import { useTranslate } from '@repo/shared/hooks'
import { FormSchema, useAppForm } from '@repo/ui/hooks'

import { useQuickKycSchema } from '@repo/auth/schemas'
import { DAYS, MONTHS } from '@repo/shared/variables/validations'
import { useAgentStartVideoKyc } from '@repo/services'
import {
  Button,
  Form,
  FormField,
  FormItemWrapper,
  FormMessage,
  Input,
  Row,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components'
import { twMerge } from 'tailwind-merge'
import { IQuickKycFormProps } from './quick-kyc.types'
import { useErrorHandler } from '@repo/shared/hooks'
import { handleNumericInput, handlePasteInput } from '@repo/tools'

export const QuickKycForm = (props: IQuickKycFormProps) => {
  const { onSuccess, mobileOrEmail } = props
  const { t } = useTranslate()
  const { schema } = useQuickKycSchema()
  const { errorHandler } = useErrorHandler()
  const { form } = useAppForm({
    formSchema: schema,
  })
  const kycMutation = useAgentStartVideoKyc({ invalidate: true })

  const onSubmit = async (data: FormSchema<typeof schema>) => {
    kycMutation.mutate(
      {
        nationalID: data.nationalCode,
        birthdate: {
          year: Number(data.year),
          month: Number(data.month),
          day: Number(data.day),
        },
      },
      {
        onSuccess: (result) => {
          const workflowID = result.data?.workflowID
          const uploadLink = result.data?.uploadLink

          if (workflowID && uploadLink) {
            onSuccess(mobileOrEmail, result.data)
          } else {
            errorHandler(new Error('Failed to get workflowID or uploadLink'), {
              type: 'toast',
            })
          }
        },
        onError: errorHandler,
      },
    )
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className='space-y-3xl w-full'
      >
        <Row className='gap-xs items-end w-full'>
          <FormField
            control={form.control}
            name='day'
            displayValidation={false}
            render={({ field }) => (
              <Select {...field} onValueChange={field.onChange}>
                <FormItemWrapper title={t('quickKyc_birthDate')} inputSize='lg'>
                  <SelectTrigger inputSize='lg' className='!m-0'>
                    <SelectValue placeholder={t('common_day')} />
                  </SelectTrigger>
                </FormItemWrapper>
                <SelectContent>
                  {DAYS.map((day) => (
                    <SelectItem key={day.value} value={day.value}>
                      {day.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />

          <FormField
            control={form.control}
            name='month'
            displayValidation={false}
            render={({ field }) => (
              <Select {...field} onValueChange={field.onChange}>
                <FormItemWrapper inputSize='lg'>
                  <SelectTrigger inputSize='lg' className='!m-0'>
                    <SelectValue placeholder={t('common_month')} />
                  </SelectTrigger>
                </FormItemWrapper>
                <SelectContent>
                  {MONTHS.map((month) => (
                    <SelectItem key={month.value} value={month.value}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />

          <FormField
            control={form.control}
            name='year'
            displayValidation={false}
            render={({ field }) => (
              <FormItemWrapper inputSize='lg'>
                <Input
                  placeholder={t('common_year')}
                  autoComplete='off'
                  inputSize='lg'
                  variant='fill'
                  className={twMerge('w-full', field.value && 'ltr')}
                  {...field}
                  onKeyDown={(e) => handleNumericInput(e, 4)}
                  onPaste={(e) => handlePasteInput(e, 4)}
                />
              </FormItemWrapper>
            )}
          />
        </Row>

        {(form?.formState?.errors?.year?.message ||
          form?.formState?.errors?.month?.message ||
          form?.formState?.errors?.day?.message) && (
          <FormMessage
            color='text-negative-surface-100'
            size='sm'
            className='!-mt-2xl'
          >
            {String(
              form?.formState?.errors?.year?.message ??
                form?.formState?.errors?.month?.message ??
                form?.formState?.errors?.day?.message ??
                '',
            ) || '\u00A0'}
          </FormMessage>
        )}

        <FormField
          control={form.control}
          name='nationalCode'
          render={({ field }) => (
            <FormItemWrapper
              title={t('quickKyc_nationalCode')}
              caption={t('quickKyc_nationalCode_hint')}
              inputSize='lg'
            >
              <Input
                autoComplete='off'
                inputSize='lg'
                variant='fill'
                className='ltr'
                {...field}
                onKeyDown={(e) => handleNumericInput(e, 10)}
                onPaste={(e) => handlePasteInput(e, 10)}
              />
            </FormItemWrapper>
          )}
        />

        <Button
          className='w-full'
          type='submit'
          variant='fill'
          size='lg'
          loading={kycMutation.isPending}
        >
          {t('quickKyc_button')}
        </Button>
      </form>
    </Form>
  )
}
